import { useState, useRef } from 'react'
import { Loader2, ExternalLink, CheckCircle2, Link } from 'lucide-react'
import { usePairing } from '@/hooks/use-pairing'
import { APP_BASE_URL } from '@/lib/constants'

// ── 6-digit code input ────────────────────────────────────────────────────────
// Ported from packages/snap/src/components/Onboarding.tsx

function CodeInput({ onComplete, disabled }: { onComplete: (code: string) => void; disabled: boolean }) {
  const [digits, setDigits] = useState(['', '', '', '', '', ''])
  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  function handleChange(i: number, value: string) {
    // Handle paste of multiple characters
    if (value.length > 1) {
      const cleaned = value.replace(/\D/g, '').slice(0, 6)
      const next = ['', '', '', '', '', '']
      for (let j = 0; j < 6; j++) {
        next[j] = cleaned[j] ?? ''
      }
      setDigits(next)
      const code = next.join('')
      if (code.length === 6) onComplete(code)
      refs[Math.min(cleaned.length - 1, 5)]?.current?.focus()
      return
    }

    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[i] = digit
    setDigits(next)

    if (digit && i < 5) refs[i + 1]?.current?.focus()

    const code = next.join('')
    if (code.length === 6 && !next.includes('')) onComplete(code)
  }

  function handleKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs[i - 1]?.current?.focus()
    }
  }

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className="w-10 h-12 text-center text-xl font-bold font-mono border-2 rounded-lg outline-none transition-colors border-secondary-200 focus:border-primary-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
        />
      ))}
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────────

interface PairingScreenProps {
  onPaired: () => void
}

export function PairingScreen({ onPaired }: PairingScreenProps) {
  const { loading, error, success, orgName, userName, pair } = usePairing(onPaired)

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-5 flex flex-col gap-5">
        {/* Logo + heading */}
        <div className="flex flex-col items-center gap-2 pt-2">
          <div className="w-12 h-12 rounded-xl bg-brand-gradient flex items-center justify-center shadow-brand">
            <Link size={22} className="text-white" />
          </div>
          <h1 className="text-base font-semibold text-secondary-900">Pair with Athenty</h1>
          <p className="text-xs text-secondary-500 text-center max-w-[220px]">
            Enter the pairing code from your Athenty portal to connect the add-in.
          </p>
        </div>

        {/* Code input */}
        {!success ? (
          <div className="space-y-4">
            <CodeInput onComplete={(code) => void pair(code)} disabled={loading} />

            {loading && (
              <div className="flex items-center justify-center gap-2 text-xs text-secondary-500">
                <Loader2 size={13} className="animate-spin" />
                Verifying code…
              </div>
            )}

            {error && (
              <p className="text-center text-xs text-error-DEFAULT">{error}</p>
            )}

            <div className="text-center">
              <a
                href={`${APP_BASE_URL}/settings/general`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600 transition-colors"
              >
                Get your pairing code from Athenty
                <ExternalLink size={11} />
              </a>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-4">
            <CheckCircle2 size={36} className="text-success-DEFAULT" />
            <p className="text-sm font-semibold text-secondary-900">Connected!</p>
            {userName && (
              <p className="text-xs text-secondary-600">{userName}</p>
            )}
            {orgName && (
              <p className="text-xs text-secondary-400">{orgName}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
